import { createVerifier, challengeFor, randomUrlSafe } from './pkce';
import {
  buildAuthorizeUrl,
  exchangeCode,
  refreshTokens,
  revokeToken,
  type RuntimeConfig,
  type TokenSet,
} from './oauth';

/**
 * Token storage and the sign-in/sign-out lifecycle.
 *
 * Tokens live in module memory and are never persisted. A hard refresh therefore loses the session
 * and redirects to /oauth2/authorize — but Cognito's own session cookie makes that a no-interaction
 * round trip, so the user sees a flash rather than a login prompt. The trade buys a real property:
 * the refresh token, which is valid for the pool's default 30 days, never touches disk where an XSS
 * could read it.
 */

const VERIFIER_KEY = 'broker.pkce.verifier';
const STATE_KEY = 'broker.pkce.state';
const RETURN_TO_KEY = 'broker.returnTo';

let tokens: TokenSet | undefined;
let refreshTimer: ReturnType<typeof setTimeout> | undefined;
let onExpired: (() => void) | undefined;

export function setTokens(next: TokenSet | undefined, config?: RuntimeConfig): void {
  tokens = next;
  if (refreshTimer) clearTimeout(refreshTimer);
  refreshTimer = undefined;
  if (next && config) scheduleRefresh(config);
}

export function getTokens(): TokenSet | undefined {
  return tokens;
}

export function onSessionExpired(callback: () => void): void {
  onExpired = callback;
}

function scheduleRefresh(config: RuntimeConfig): void {
  if (!tokens?.refreshToken) return;
  // Two minutes of headroom, and never less than five seconds out even for a near-expired token.
  const delay = Math.max(tokens.expiresAt - Date.now() - 120_000, 5_000);
  refreshTimer = setTimeout(() => {
    void refreshNow(config).catch(() => onExpired?.());
  }, delay);
}

export async function refreshNow(config: RuntimeConfig): Promise<TokenSet> {
  if (!tokens?.refreshToken) throw new Error('No refresh token');
  const next = await refreshTokens(config, tokens.refreshToken);
  setTokens(next, config);
  return next;
}

/**
 * Starts the sign-in redirect.
 *
 * sessionStorage rather than localStorage for the verifier and state: it is scoped to this tab and
 * dies with it, so a verifier cannot outlive the flow it belongs to or leak into a sibling tab. An
 * XSS on this origin could read it — but the same XSS could read the tokens and just call the API,
 * so the verifier is not the marginal risk. In-memory is impossible here: /oauth2/authorize is a
 * full-page navigation, so any memory is gone before the code comes back.
 */
export async function beginSignIn(config: RuntimeConfig, returnTo?: string): Promise<void> {
  const verifier = createVerifier();
  const state = randomUrlSafe(16);

  sessionStorage.setItem(VERIFIER_KEY, verifier);
  sessionStorage.setItem(STATE_KEY, state);
  if (returnTo) sessionStorage.setItem(RETURN_TO_KEY, returnTo);

  window.location.assign(buildAuthorizeUrl(config, { state, challenge: await challengeFor(verifier) }));
}

/** Completes the flow at /auth/callback. Returns the path the user was originally heading to. */
export async function completeSignIn(config: RuntimeConfig, search: string): Promise<string> {
  const params = new URLSearchParams(search);
  const verifier = sessionStorage.getItem(VERIFIER_KEY);
  const expectedState = sessionStorage.getItem(STATE_KEY);
  const returnTo = sessionStorage.getItem(RETURN_TO_KEY) ?? '/';

  try {
    const error = params.get('error');
    if (error) {
      // Cognito surfaces the trigger's refusal here. Its message is deliberately opaque; the real
      // reason is in the pre-token-generation log group.
      throw new Error(params.get('error_description') || error);
    }

    const code = params.get('code');
    if (!code) throw new Error('No authorization code in the callback URL.');
    if (!verifier) throw new Error('Sign-in did not start in this tab. Try again.');
    if (!expectedState || params.get('state') !== expectedState) {
      throw new Error('State mismatch — the sign-in response did not match this tab’s request.');
    }

    setTokens(await exchangeCode(config, { code, verifier }), config);
    return returnTo;
  } finally {
    // Always, even on failure: a verifier that survives a failed exchange is reusable.
    sessionStorage.removeItem(VERIFIER_KEY);
    sessionStorage.removeItem(STATE_KEY);
    sessionStorage.removeItem(RETURN_TO_KEY);
  }
}

/**
 * Local sign-out.
 *
 * What this clears, and what it does not:
 *   - in-memory tokens                                          cleared
 *   - the refresh token's server-side validity                  cleared, if revoke succeeded
 *     (which also invalidates access tokens minted from it)
 *   - the already-issued ID token                               NOT cleared; valid until exp
 *   - Cognito's hosted-UI session cookie on the broker domain   NOT cleared
 *   - the Microsoft session on login.microsoftonline.com        NOT cleared
 *
 * The last two are why the signed-out screen carries a shared-workstation warning: a new console
 * session can still be obtained without a password prompt until those expire. A true Cognito
 * /logout would clear its cookie, but that endpoint requires logout_uri to be pre-registered in the
 * client's LogoutURLs, and CreateUserPoolClientCommand in src/core/onboarding.ts never sets them.
 * Enabling it would mean changing shared core code and its tests.
 */
export async function signOutLocally(config: RuntimeConfig): Promise<void> {
  const refreshToken = tokens?.refreshToken;
  setTokens(undefined);
  if (refreshToken) await revokeToken(config, refreshToken);
}

/** The tenant-scoped Microsoft sign-out link offered on the signed-out screen. */
export function microsoftLogoutUrl(config: RuntimeConfig): string {
  return `https://login.microsoftonline.com/${config.entraTenantId}/oauth2/v2.0/logout`;
}
